/**
 * @author David Roman <david@inarix.com>
 * @file Description
 * @desc Created on 2020-11-16 10:45:38 am
 * @copyright Inarix
 */

import {
  User,
  haveAccess,
  signIn,
  refreshToken,
  recoverPassword,
  resetPassword,
} from "../../actions/authentication/authentication";

import { createSlice } from "@reduxjs/toolkit";
import pjson from "../../../../package.json";
import { PURGE } from "redux-persist";
import { logOut } from "../layout";

export interface AuthState {
  user: User | null;
  username: string;
  password: string;
  authenticated: boolean;
  recoverEmail: string;
  newPassword: string;
  resetPaswordSuccess: boolean;
  emailSent: boolean;
  msg: string;
  loading: boolean;
  rememberMe: boolean;
  error: string;
  errorAuthenticated: null | number;
  errorRefreshToken: string;
  noAccess: null | number;
  refreshTimeoutId: undefined | number;
  isThePortalVersionDifferent: boolean;
  isViewerOnly: boolean;
  portalVersion: {
    version: number | string;
  };
  setTranslation: {
    Fr: string;
    Eng: string;
    Es: string;
    De: string;
  };
  companyName: null | string;
  orgId: null | number;
}

export const initialState: AuthState = {
  user: null,
  username: "",
  password: "",
  authenticated: false,
  recoverEmail: "",
  newPassword: "",
  resetPaswordSuccess: false,
  emailSent: false,
  msg: "",
  loading: false,
  rememberMe: false,
  error: "",
  errorRefreshToken: "",
  errorAuthenticated: null,
  noAccess: null,
  refreshTimeoutId: undefined,
  isThePortalVersionDifferent: false,
  isViewerOnly: false,
  portalVersion: {
    version: `${pjson.version}`,
  },
  setTranslation: {
    Fr: "fr",
    Eng: "en",
    Es: "es",
    De: "de",
  },
  companyName: null,
  orgId: null,
};

export const AuthSlice = createSlice({
  name: "AuthReducer",
  initialState,
  reducers: {
    setOrgId(state, action) {
      return {
        ...state,
        orgId: action.payload,
      };
    },
    setCompanyName(state, action) {
      return {
        ...state,
        companyName: action.payload,
      };
    },
    setResetPasswordSuccess(state, action) {
      state.resetPaswordSuccess = action.payload;
    },

    setToken(state, action) {
      state.loading = false;
      state.user = { token: action.payload };
      state.authenticated = true;
      state.error = "";
    },
    setEmailSent(state, action) {
      state.emailSent = action.payload;
    },
    setEmailRecover(state, action) {
      state.recoverEmail = action.payload;
    },
    setNewPassword(state, action) {
      state.newPassword = action.payload;
    },

    setRememberMe(state, action) {
      state.rememberMe = action.payload;
    },

    isViewerOnlyInfo(state, action) {
      state.isViewerOnly = action.payload;
    },
    disableErrorLogin(state) {
      state.error = "";
      state.errorAuthenticated = null;
    },

    resetState(state) {
      state.user = null;
      state.authenticated = false;
      state.msg = "";
      state.loading = false;
      state.error = "";
      state.errorAuthenticated = null;
      state.noAccess = null;
      state.refreshTimeoutId = undefined;
      state.isViewerOnly = false;
      state.recoverEmail = "";
      state.newPassword = "";
    },

    signOut(state) {
      state.user = null;
      state.authenticated = false;
      state.loading = false;
      state.msg = "";
      state.noAccess = null;
      state.recoverEmail = "";
      state.newPassword = "";
    },
    setRefreshTimeout(state, action) {
      state.refreshTimeoutId = action.payload ?? undefined;
    },

    isPortalVersionDifferent(state, action) {
      state.isThePortalVersionDifferent = action.payload;
    },
    setUsername(state, action) {
      state.username = action.payload;
    },
    setPassword(state, action) {
      state.password = action.payload;
    },
    loading(state, action) {
      state.loading = action.payload;
    },
    setPortalVersion(state, action) {
      state.portalVersion.version = action.payload;
    },
  },

  extraReducers(builder) {
    builder.addCase(PURGE, () => {
      return initialState;
    });

    builder
      .addCase(resetPassword.pending, (state) => {
        state.loading = true;
        state.resetPaswordSuccess = false;
      })
      .addCase(resetPassword.fulfilled, (state) => {
        state.loading = false;
        state.resetPaswordSuccess = true;

        state.error = "";
      })
      .addCase(resetPassword.rejected, (state, action: any) => {
        state.resetPaswordSuccess = false;
        state.loading = false;
        state.error =
          action.payload?.response?.data?.message || action?.payload;
      });

    builder
      .addCase(recoverPassword.pending, (state) => {
        state.loading = true;
        state.emailSent = false;
      })
      .addCase(recoverPassword.fulfilled, (state) => {
        state.loading = false;
        state.emailSent = true;
        state.recoverEmail = "";
        state.error = "";
      })
      .addCase(recoverPassword.rejected, (state, action: any) => {
        state.emailSent = false;
        state.loading = false;
        state.recoverEmail = "";
        state.error =
          action.payload?.response?.data?.message || action?.payload;
      });

    builder
      .addCase(signIn.pending, (state) => {
        state.loading = true;
      })

      .addCase(signIn.rejected, (state, action: any) => {
        state.loading = false;
        state.authenticated = false;
        state.user = null;
        state.error = action.payload;
      });

    builder.addCase(haveAccess.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(haveAccess.fulfilled, (state, action) => {
      state.loading = false;
      state.msg = action.payload;
    });
    builder.addCase(haveAccess.rejected, (state, action: any) => {
      state.loading = false;
      state.errorAuthenticated = action.payload?.response?.status;
      state.noAccess = action.payload?.response?.status;
    });

    builder.addCase(refreshToken.pending, (state) => {
      state.loading = true;
      state.errorRefreshToken = "";
    });

    builder.addCase(refreshToken.fulfilled, (state, action) => {
      state.loading = false;
      state.authenticated = true;
      state.user = { token: action.payload };
      state.errorRefreshToken = "";
    });

    builder.addCase(refreshToken.rejected, (state, action) => {
      state.loading = false;
      state.errorRefreshToken = action.payload as string;
    });

    builder.addCase(logOut, () => {
      return initialState;
    });
  },
});

export const {
  setToken,
  setOrgId,
  setCompanyName,
  setResetPasswordSuccess,
  setEmailSent,
  setEmailRecover,
  setNewPassword,
  loading,
  resetState,
  setUsername,
  setPassword,
  isPortalVersionDifferent,
  setRefreshTimeout,
  signOut,
  disableErrorLogin,
  setRememberMe,
  isViewerOnlyInfo,
  setPortalVersion,
} = AuthSlice.actions;

export const AuthReducer = AuthSlice.reducer;
